import React, { useState, useEffect } from 'react';
import { formatDate, todayStr, daysAgoStr } from '../utils/dateUtils';
import {
  Search,
  Download,
  Calendar,
  User,
  CreditCard,
  FileText,
  Loader,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
  KeyRound,
  ClipboardList,
  Phone,
  MapPin,
  Building,
  CheckCircle,
  Menu,
  X
} from 'lucide-react';
import MaskedDatePicker from './MaskedDatePicker';
import { parse, format } from 'date-fns';
import * as XLSX from 'xlsx';

const CSReportComponent = () => {
  // Sidebar & page state
  const [activeMenu, setActiveMenu] = useState('cs-report');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // CS Report states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [searchProgress, setSearchProgress] = useState({ current: 0, total: 0, employeeName: '' });

  // Reset Password states
  const [agentCode, setAgentCode] = useState('');
  const [agentData, setAgentData] = useState(null);
  const [agentLoading, setAgentLoading] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  // Get default dates - 7 days ago to today
  const getDefaultDates = () => ({
    fromDate: daysAgoStr(7),
    toDate: todayStr(),
  });

  const [filters, setFilters] = useState({
    ...getDefaultDates(),
    saleName: 'ศรินทร ย่องนุ่น',
    paymentStatus: 'approved',
    page: 1,
    limit: 100
  });

  const paymentStatusOptions = [
    { value: 'approved', label: 'คำสั่งซื้อเรียบร้อย' },
    { value: 'confirm', label: 'ลูกค้ายืนยันแล้ว' },
    { value: 'waiting', label: 'รอการอนุมัติจากลูกค้า' },
    { value: 'cancel', label: 'ยกเลิกใบเสนอราคา' }
  ];

  const salesPersonOptions = [
    { value: '', label: 'ทั้งหมด' },
    { value: 'ศรินทร ย่องนุ่น', label: 'ศรินทร ย่องนุ่น' },
    { value: 'กชพร วรดานันทชัย', label: 'กชพร วรดานันทชัย' },
    { value: 'อัชราภรณ์ สังวรการ', label: 'อัชราภรณ์ สังวรการ' },
    { value: 'กฤชนนท์ จันทร์ไพจิตร', label: 'กฤชนนท์ จันทร์ไพจิตร' },
    { value: 'อลิษา กิจเอื้อวิริยะ', label: 'อลิษา กิจเอื้อวิริยะ' },
    { value: 'สุพรรษา จักรโสภา', label: 'สุพรรษา จักรโสภา' },
    { value: 'อนุชตรา ใจดี', label: 'อนุชตรา ใจดี' },
    { value: 'จักรพงษ์ สิงห์จานุสงค์', label: 'จักรพงษ์ สิงห์จานุสงค์' },
    { value: 'แจ๊ค กลึงกลม', label: 'แจ๊ค กลึงกลม' },
    { value: 'พุธิตา แววดี', label: 'พุธิตา แววดี' },
    { value: 'กิตติศักดิ์ เสมอภักดิ์', label: 'กิตติศักดิ์ เสมอภักดิ์' }
  ];

  const searchSingleEmployee = async (saleName, page = 1) => {
    const requestBody = {
      fromDate: filters.fromDate,
      toDate: filters.toDate,
      saleName: saleName,
      paymentStatus: [filters.paymentStatus],
      page: page,
      limit: filters.limit
    };

    const response = await fetch('https://apisaleeasy.psisat.com/api/emp_quotation/search/advanced', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    const result = await response.json();
    return result;
  };

  const searchQuotations = async () => {
    if (!filters.fromDate || !filters.toDate) {
      setError('กรุณาเลือกช่วงวันที่');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // If "ทั้งหมด" (empty string) is selected, search all employees
      if (!filters.saleName) {
        const allEmployees = salesPersonOptions.filter(emp => emp.value !== '');
        let allData = [];
        let totalRecords = 0;
        
        // Initialize progress
        setSearchProgress({ current: 0, total: allEmployees.length, employeeName: '' });
        
        // Search each employee
        for (let i = 0; i < allEmployees.length; i++) {
          const employee = allEmployees[i];
          
          // Update progress
          setSearchProgress({ current: i + 1, total: allEmployees.length, employeeName: employee.label });
          
          try {
            const result = await searchSingleEmployee(employee.value, 1);
            if (result.status && result.data && result.data.data) {
              allData.push(...result.data.data);
              totalRecords += result.data.pagination?.total || 0;
            }
          } catch (err) {
            console.error(`Error searching for employee ${employee.label}:`, err);
          }
        }
        
        // Reset progress
        setSearchProgress({ current: 0, total: 0, employeeName: '' });

        // Sort by creation date descending
        allData.sort((a, b) => new Date(b.qn_quotation_sale_date) - new Date(a.qn_quotation_sale_date));

        // Manual pagination for combined data
        const startIndex = (filters.page - 1) * filters.limit;
        const endIndex = startIndex + filters.limit;
        const paginatedData = allData.slice(startIndex, endIndex);
        
        const totalPages = Math.ceil(allData.length / filters.limit);
        
        setData(paginatedData);
        setPagination({
          current_page: filters.page,
          per_page: filters.limit,
          total: allData.length,
          total_pages: totalPages,
          has_prev: filters.page > 1,
          has_next: filters.page < totalPages
        });
      } else {
        // Single employee search (original logic)
        const result = await searchSingleEmployee(filters.saleName, filters.page);
        
        if (result.status && result.data) {
          // Log first item to check available fields
          if (result.data.data && result.data.data.length > 0) {
            console.log('Quotation item fields:', Object.keys(result.data.data[0]));
            console.log('First item full data:', result.data.data[0]);
          }
          setData(result.data.data || []);
          setPagination(result.data.pagination || {});
        } else {
          setError(result.result_desc || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
          setData([]);
          setPagination({});
        }
      }
    } catch (err) {
      console.error('Error searching quotations:', err);
      setError('เกิดข้อผิดพลาดในการเชื่อมต่อ API');
      setData([]);
      setPagination({});
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  const handlePaymentStatusChange = (status) => {
    handleFilterChange('paymentStatus', status);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (pagination.total_pages || 1)) {
      handleFilterChange('page', newPage);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getPaymentStatusBadge = (status, statusTh) => {
    const statusColors = {
      'approved': 'bg-green-100 text-green-800',
      'confirm': 'bg-blue-100 text-blue-800',
      'waiting': 'bg-yellow-100 text-yellow-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'cancel': 'bg-red-100 text-red-800',
      'cancelled': 'bg-red-100 text-red-800'
    };

    // Override display text for status
    let displayText = statusTh || status;
    if (status === 'confirm') {
      displayText = 'ลูกค้ายืนยันแล้ว';
    } else if (status === 'approved') {
      displayText = 'คำสั่งซื้อเรียบร้อย';
    } else if (status === 'waiting' || status === 'pending') {
      displayText = 'รอการอนุมัติจากลูกค้า';
    } else if (status === 'cancel' || status === 'cancelled') {
      displayText = 'ยกเลิกใบเสนอราคา';
    }

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {displayText}
      </span>
    );
  };

  const getTechnicianTypeName = (majorType) => {
    const typeMap = {
      'ma': 'รายใหญ่',
      'normal': 'ปกติ',
      'new': 'ใหม่',
      'cash': 'ลูกค้าทั่วไป'
    };
    return typeMap[majorType] || majorType || '-';
  };

  const exportToExcel = async () => {
    if (!filters.fromDate || !filters.toDate) {
      alert('กรุณาเลือกช่วงวันที่');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // ค้นหาข้อมูลใหม่ตาม filter ปัจจุบันก่อน export เสมอ
      let freshData = [];

      if (!filters.saleName) {
        // ค้นหาทุกพนักงาน
        const allEmployees = salesPersonOptions.filter(emp => emp.value !== '');
        setSearchProgress({ current: 0, total: allEmployees.length, employeeName: '' });

        for (let i = 0; i < allEmployees.length; i++) {
          const employee = allEmployees[i];
          setSearchProgress({ current: i + 1, total: allEmployees.length, employeeName: employee.label });
          try {
            const result = await searchSingleEmployee(employee.value, 1);
            if (result.status && result.data && result.data.data) {
              freshData.push(...result.data.data);
            }
          } catch (err) {
            console.error(`Error searching for employee ${employee.label}:`, err);
          }
        }
        setSearchProgress({ current: 0, total: 0, employeeName: '' });
        freshData.sort((a, b) => new Date(b.qn_quotation_sale_date) - new Date(a.qn_quotation_sale_date));
      } else {
        // ค้นหาพนักงานคนเดียว
        const result = await searchSingleEmployee(filters.saleName, 1);
        if (result.status && result.data && result.data.data) {
          freshData = result.data.data;
        }
      }

      // อัพเดท state ด้วยข้อมูลใหม่
      setData(freshData);

      if (!freshData.length) {
        alert('ไม่มีข้อมูลสำหรับ Export ในช่วงวันที่ที่เลือก');
        setLoading(false);
        return;
      }

      // Fetch detailed quotation data for each item
      const detailedData = await Promise.all(
        freshData.map(async (item) => {
          try {
            const response = await fetch(`https://apisaleeasy.psisat.com/api/emp_quotation/${item.qn_quotation_no_code}`);
            const result = await response.json();

            if (result.status && result.data && result.data.products) {
              return {
                ...item,
                products: result.data.products
              };
            }
            return {
              ...item,
              products: []
            };
          } catch (error) {
            console.error(`Error fetching details for ${item.qn_quotation_no_code}:`, error);
            return {
              ...item,
              products: []
            };
          }
        })
      );

      // Fetch technician type for each unique customer code
      const uniqueCustCodes = [...new Set(detailedData.map(item => item.technician_code).filter(Boolean))];
      const technicianTypeMap = {};

      await Promise.all(
        uniqueCustCodes.map(async (custCode) => {
          // รหัส 5890045 = ลูกค้าทั่วไป ไม่ต้องเรียก API
          if (custCode === '5890045') {
            technicianTypeMap[custCode] = 'cash';
            return;
          }
          try {
            const response = await fetch('https://apisaleeasy.psisat.com/api/technician/get-type', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ technician_code: String(custCode) })
            });
            const result = await response.json();
            if (result.status && result.data) {
              technicianTypeMap[custCode] = result.data.technician_major;
            }
          } catch (error) {
            console.error(`Error fetching technician type for ${custCode}:`, error);
          }
        })
      );

      // Fetch customer ERP data for each unique customer code
      const customerDataMap = {};
      await Promise.all(
        uniqueCustCodes.map(async (custCode) => {
          try {
            const response = await fetch('https://apisaleeasy.psisat.com/api/customererp/by-custno', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ custNo: String(custCode) })
            });
            const result = await response.json();
            if (result.status && result.data) {
              customerDataMap[custCode] = result.data;
            }
          } catch (error) {
            console.error(`Error fetching customer data for ${custCode}:`, error);
          }
        })
      );

      // Fetch invoice & shipment data for approved items
      const shipmentDataMap = {};
      const approvedItems = detailedData.filter(item => item.payment_status === 'approved' && item.qn_quotation_runnumber);

      await Promise.all(
        approvedItems.map(async (item) => {
          try {
            // Step 1: Get invoice from quotation runnumber
            const invoiceRes = await fetch(`https://apisaleeasy.psisat.com/api/quotation-invoice/quotation/${item.qn_quotation_runnumber}/invoices`);
            const invoiceResult = await invoiceRes.json();

            if (invoiceResult.status && invoiceResult.data && invoiceResult.data.invoices && invoiceResult.data.invoices.length > 0) {
              const invoiceNumbers = [];
              const shippingInfoList = [];

              for (const inv of invoiceResult.data.invoices) {
                const invoiceNumber = (inv.mapping?.invoice_number || '').trim();
                const companyCode = (inv.mapping?.invoice_company_code || '').trim();
                if (!invoiceNumber || !companyCode) continue;

                invoiceNumbers.push(invoiceNumber);

                // Step 2: Get shipment from invoice number
                try {
                  const shipRes = await fetch(`https://apisaleeasy.psisat.com/api/shipments/invoice/${companyCode}/${invoiceNumber}`);
                  const shipResult = await shipRes.json();

                  if (shipResult.status && shipResult.data && shipResult.data.length > 0) {
                    for (const shipment of shipResult.data) {
                      if (shipment.tracking && shipment.tracking.length > 0) {
                        // มี tracking → เอา tracking_number + notes มาแสดง
                        shipment.tracking
                          .filter(t => t.tracking_number)
                          .forEach(t => {
                            shippingInfoList.push({ trackingType: t.tracking_type || 'ไม่ระบุ', trackingNumber: t.tracking_number, notes: t.notes || '' });
                          });
                      } else {
                        // ไม่มี tracking → เอา notes จาก shipment มาแสดง
                        if (shipment.notes) {
                          shippingInfoList.push({ trackingType: '', trackingNumber: '', notes: shipment.notes });
                        }
                      }
                    }
                  }
                } catch (shipErr) {
                  console.error(`Error fetching shipment for invoice ${invoiceNumber}:`, shipErr);
                }
              }

              shipmentDataMap[item.qn_quotation_runnumber] = {
                invoiceNumbers: invoiceNumbers.join(', '),
                shippingList: shippingInfoList
              };
            }
          } catch (error) {
            console.error(`Error fetching invoice for runnumber ${item.qn_quotation_runnumber}:`, error);
          }
        })
      );

      // สร้างข้อมูลแยก 2 sheet: Invoice และ ขนส่ง
      const invoiceHeaders = [
        'วันที่', 'Invoice', 'รหัสลูกค้า', 'ประเภทลูกค้า', 'ลูกค้า',
        'เบอร์ลูกค้า', 'เบอร์ผู้รับสินค้า', 'สินค้า', 'จำนวน(ชุด)',
        'จำนวน(ชิ้น)', 'ราคา/ชุด', 'ราคารวม', 'พนักงานขาย', 'สถานะการชำระ'
      ];

      const shippingHeaders = [
        'วันที่', 'Invoice', 'รหัสลูกค้า', 'ประเภทลูกค้า', 'ลูกค้า',
        'เบอร์ลูกค้า', 'เบอร์ผู้รับสินค้า', 'พนักงานขาย',
        'ประเภทขนส่ง', 'เลขจัดส่ง/Tracking', 'รายละเอียดจัดส่ง'
      ];

      const invoiceData = [];
      const shippingData = [];

      detailedData.forEach(item => {
        const custCode = item.technician_code || '';
        const techType = getTechnicianTypeName(technicianTypeMap[custCode]);
        const customerData = customerDataMap[custCode] || {};
        const customerPhone = customerData.Phone || '';
        const shipmentInfo = shipmentDataMap[item.qn_quotation_runnumber] || {};
        const invoiceNumbers = shipmentInfo.invoiceNumbers || '';
        const shippingList = shipmentInfo.shippingList || [];
        const products = (item.products && item.products.length > 0) ? item.products : [];

        // ข้อมูลกลาง
        const commonData = [
          formatDate(item.qn_quotation_sale_date),
          invoiceNumbers,
          custCode,
          techType,
          item.qn_quotation_name || '',
          customerPhone,
          item.qn_quotation_phone || ''
        ];

        // Sheet Invoice - แยกแต่ละสินค้าเป็นแต่ละ row
        if (products.length > 0) {
          products.forEach(product => {
            const qty = product.product_quantity || 0;
            const totalPieces = qty * (product.factory_per_pack || 1);
            const totalPrice = product.product_rate_pice || 0;
            const unitPrice = qty > 0 ? totalPrice / qty : 0;
            invoiceData.push([
              ...commonData,
              product.other1psi1 || '',
              qty, totalPieces, unitPrice, totalPrice,
              item.qn_quotation_sale_name || '',
              item.payment_status_th || item.payment_status || ''
            ]);
          });
        } else {
          invoiceData.push([
            ...commonData, '', '', '', '', '',
            item.qn_quotation_sale_name || '',
            item.payment_status_th || item.payment_status || ''
          ]);
        }

        // Sheet ขนส่ง - แยกแต่ละ tracking เป็นแต่ละ row
        if (shippingList.length > 0) {
          shippingList.forEach(shipping => {
            shippingData.push([
              ...commonData,
              item.qn_quotation_sale_name || '',
              shipping.trackingType || 'ไม่ระบุ',
              shipping.trackingNumber || '',
              shipping.notes || ''
            ]);
          });
        } else {
          shippingData.push([
            ...commonData,
            item.qn_quotation_sale_name || '',
            'ไม่ระบุ', '', ''
          ]);
        }
      });

      // สร้าง Excel workbook 2 sheets
      const wb = XLSX.utils.book_new();

      const wsInvoice = XLSX.utils.aoa_to_sheet([invoiceHeaders, ...invoiceData]);
      XLSX.utils.book_append_sheet(wb, wsInvoice, 'Invoice');

      const wsShipping = XLSX.utils.aoa_to_sheet([shippingHeaders, ...shippingData]);
      XLSX.utils.book_append_sheet(wb, wsShipping, 'ขนส่ง');

      XLSX.writeFile(wb, `CS_Report_Detail_${filters.fromDate}_${filters.toDate}.xlsx`);
      
    } catch (error) {
      console.error('Export error:', error);
      alert('เกิดข้อผิดพลาดในการ Export');
    } finally {
      setLoading(false);
    }
  };

  // Agent search function
  const searchAgent = async () => {
    if (!agentCode.trim()) {
      setAgentError('กรุณากรอกรหัสช่าง');
      return;
    }

    setAgentLoading(true);
    setAgentError('');
    setAgentData(null);
    setResetSuccess(false);

    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/technician/by-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: agentCode.trim() })
      });
      const result = await response.json();

      if (result.status && result.data) {
        setAgentData(result.data);
      } else {
        setAgentError(result.result_desc || 'ไม่พบข้อมูลช่าง');
      }
    } catch (err) {
      console.error('Error searching agent:', err);
      setAgentError('เกิดข้อผิดพลาดในการเชื่อมต่อ API');
    } finally {
      setAgentLoading(false);
    }
  };

  // Reset technician psi_connect_password
  const resetAgentPassword = async () => {
    if (!agentData) return;

    if (!newPassword.trim()) {
      alert('กรุณากรอกรหัสผ่านใหม่');
      return;
    }

    const confirmed = window.confirm(
      `ยืนยันการรีเซ็ตรหัสผ่านของ\n${agentData.technician_name} ${agentData.technician_surname || ''}\nรหัส: ${agentData.technician_code}`
    );
    if (!confirmed) return;

    setResetLoading(true);
    setResetSuccess(false);

    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/technician/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ technician_code: agentData.technician_code, new_password: newPassword.trim() })
      });
      const result = await response.json();

      if (result.status) {
        setResetSuccess(true);
      } else {
        alert(result.result_desc || 'เกิดข้อผิดพลาดในการรีเซ็ตรหัสผ่าน');
      }
    } catch (err) {
      console.error('Error resetting password:', err);
      alert('เกิดข้อผิดพลาดในการเชื่อมต่อ API');
    } finally {
      setResetLoading(false);
    }
  };

  // Auto-search on initial load and page change
  useEffect(() => {
    if (filters.fromDate && filters.toDate) {
      searchQuotations();
    }
  }, [filters.page]);

  // Auto-search on initial load
  useEffect(() => {
    if (activeMenu === 'cs-report') {
      searchQuotations();
    }
  }, []);

  // Sidebar menu items
  const menuItems = [
    { id: 'cs-report', label: 'รายงาน CS', icon: ClipboardList },
    { id: 'reset-password', label: 'รีเซ็ตรหัสผ่านช่าง', icon: KeyRound },
  ];

  // Render Reset Password page
  const renderResetPassword = () => (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">รีเซ็ตรหัสผ่านช่าง</h1>
        <p className="text-gray-600 mt-1">ค้นหาช่างด้วยรหัสช่างและรีเซ็ตรหัสผ่านใบเสนอราคา</p>
      </div>

      {/* Search Box */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Search className="w-5 h-5" />
          ค้นหาช่าง
        </h2>
        <div className="flex gap-3">
          <input
            type="text"
            value={agentCode}
            onChange={(e) => setAgentCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && searchAgent()}
            placeholder="กรอกรหัสช่าง เช่น 6830366"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={searchAgent}
            disabled={agentLoading}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {agentLoading ? <Loader className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            ค้นหา
          </button>
        </div>
      </div>

      {/* Error */}
      {agentError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-600">{agentError}</p>
        </div>
      )}

      {/* Agent Info Card */}
      {agentData && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            ข้อมูลช่าง
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Building className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">รหัสช่าง</p>
                <p className="text-lg font-semibold text-gray-900">{agentData.technician_code}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <User className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">ชื่อ</p>
                <p className="text-lg font-semibold text-gray-900">
                  {agentData.technician_name} {agentData.technician_surname || ''}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">เบอร์โทร</p>
                <p className="text-lg font-semibold text-gray-900">{agentData.technician_phone || '-'}</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
              <MapPin className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">ที่อยู่</p>
                <p className="text-sm font-medium text-gray-900">
                  {[agentData.technician_address, agentData.technician_district, agentData.technician_province, agentData.technician_zip_code].filter(Boolean).join(' ')}
                </p>
              </div>
            </div>
          </div>

          {/* New Password Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <KeyRound className="w-4 h-4 inline mr-1" />
              รหัสผ่านใหม่
            </label>
            <input
              type="text"
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setResetSuccess(false); }}
              placeholder="กรอกรหัสผ่านใหม่"
              className="w-full md:w-80 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Reset Success Message */}
          {resetSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-green-700 font-medium">รีเซ็ตรหัสผ่านใบเสนอราคาสำเร็จ</p>
            </div>
          )}

          {/* Reset Button */}
          <button
            onClick={resetAgentPassword}
            disabled={resetLoading || !newPassword.trim()}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
          >
            {resetLoading ? <Loader className="w-5 h-5 animate-spin" /> : <KeyRound className="w-5 h-5" />}
            {resetLoading ? 'กำลังบันทึก...' : 'แก้ไขรหัสผ่าน PSI Connect / ใบเสนอราคา'}
          </button>
        </div>
      )}

      {/* Initial State */}
      {!agentLoading && !agentData && !agentError && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">ค้นหาช่างเพื่อรีเซ็ตรหัสผ่าน</h3>
          <p className="text-gray-500">กรอกรหัสช่างด้านบนแล้วกดค้นหา</p>
        </div>
      )}
    </div>
  );

  // Render CS Report page
  const renderCSReport = () => (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">รายงาน CS (Customer Service)</h1>
          <p className="text-gray-600 mt-1">ข้อมูลใบเสนอราคาและการติดตาม</p>
        </div>
      </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Search className="w-5 h-5" />
            ตัวกรองข้อมูล
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่เริ่มต้น
              </label>
              <MaskedDatePicker
                value={filters.fromDate}
                onChange={(str) => handleFilterChange('fromDate', str)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholderText="DD/MM/YYYY"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                วันที่สิ้นสุด
              </label>
              <MaskedDatePicker
                value={filters.toDate}
                onChange={(str) => handleFilterChange('toDate', str)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholderText="DD/MM/YYYY"
              />
            </div>

            {/* Sales Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                ชื่อพนักงานขาย
              </label>
              <select
                value={filters.saleName}
                onChange={(e) => handleFilterChange('saleName', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {salesPersonOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Payment Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CreditCard className="w-4 h-4 inline mr-1" />
                สถานะการชำระเงิน
              </label>
              <select
                value={filters.paymentStatus}
                onChange={(e) => handlePaymentStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {paymentStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              onClick={searchQuotations}
              disabled={loading || !filters.fromDate || !filters.toDate}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              ค้นหา
            </button>

            {/* Progress indicator for searching all employees */}
            {loading && searchProgress.total > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                <Loader className="w-4 h-4 animate-spin text-blue-600" />
                <div className="text-sm text-blue-800">
                  <div>กำลังค้นหา ({searchProgress.current}/{searchProgress.total})</div>
                  {searchProgress.employeeName && (
                    <div className="text-xs text-blue-600">พนักงาน: {searchProgress.employeeName}</div>
                  )}
                </div>
                <div className="ml-2 bg-blue-200 rounded-full w-20 h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(searchProgress.current / searchProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}

            <button
              onClick={() => {
                setFilters({
                  ...getDefaultDates(),
                  saleName: 'ศรินทร ย่องนุ่น',
                  paymentStatus: 'approved',
                  page: 1,
                  limit: 100
                });
                setData([]);
                setPagination({});
                setError('');
              }}
              className="flex items-center gap-2 px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              รีเซ็ต
            </button>

            {data.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {loading ? 'กำลัง Export...' : 'Export Excel'}
              </button>
            )}
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h4 className="text-red-800 font-medium">เกิดข้อผิดพลาด</h4>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              ×
            </button>
          </div>
        )}

        {/* Results */}
        {data.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200">
            {/* Summary */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">ผลการค้นหา</h3>
                  <p className="text-gray-600">
                    พบ {pagination.total || 0} รายการ 
                    {pagination.total_pages > 1 && (
                      <span> (หน้า {pagination.current_page || 1} จาก {pagination.total_pages || 1})</span>
                    )}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">
                    แสดง {((pagination.current_page - 1) * pagination.per_page) + 1} - {Math.min(pagination.current_page * pagination.per_page, pagination.total)} จาก {pagination.total} รายการ
                  </p>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      เลขที่ใบเสนอราคา
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      รหัสลูกค้า
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ลูกค้า
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      พนักงานขาย
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      วันที่สร้าง
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ราคารวม
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      สถานะการชำระ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      การดำเนินการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((item, index) => (
                    <tr key={`${item.qn_quotation_runnumber}-${index}`} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.qn_quotation_no_code}
                        </div>
                        <div className="text-xs text-gray-500">
                          #{item.qn_quotation_runnumber}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {item.technician_code || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {item.qn_quotation_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.qn_quotation_phone}
                        </div>
                      </td>
                      <td className="px-6 py-4 w-48">
                        <div className="text-sm text-gray-900">
                          {item.qn_quotation_sale_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {item.emp_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(item.qn_quotation_sale_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {formatCurrency(item.qn_quotation_total)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentStatusBadge(item.payment_status, item.payment_status_th)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {item.qn_url && (
                          <a
                            href={item.qn_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <FileText className="w-4 h-4" />
                            ดูใบเสนอราคา
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    หน้า {pagination.current_page} จาก {pagination.total_pages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={!pagination.has_prev}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      ก่อนหน้า
                    </button>
                    
                    {/* Page numbers */}
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                        let pageNum;
                        if (pagination.total_pages <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.current_page <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.current_page >= pagination.total_pages - 2) {
                          pageNum = pagination.total_pages - 4 + i;
                        } else {
                          pageNum = pagination.current_page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`px-3 py-2 text-sm rounded-lg ${
                              pageNum === pagination.current_page
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={!pagination.has_next}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      ถัดไป
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* No Data */}
        {!loading && data.length === 0 && filters.fromDate && filters.toDate && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่พบข้อมูล</h3>
            <p className="text-gray-500">ไม่พบข้อมูลใบเสนอราคาในช่วงวันที่ที่เลือก</p>
          </div>
        )}

        {/* Initial State */}
        {!loading && data.length === 0 && !filters.fromDate && !filters.toDate && (
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">เริ่มต้นการค้นหา</h3>
            <p className="text-gray-500">กรุณาเลือกช่วงวันที่และกดปุ่มค้นหาเพื่อดูข้อมูล</p>
          </div>
        )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-white border-r border-gray-200 shadow-lg transition-all duration-300 flex flex-col`}>
        {/* Logo & Toggle */}
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-1 hover:bg-gray-100 rounded-lg">
            {sidebarOpen ? <X className="w-5 h-5 text-gray-500" /> : <Menu className="w-5 h-5 text-gray-500" />}
          </button>
          {sidebarOpen && (
            <div className="flex items-center gap-2">
              <img src="/logo-psi2018.png" alt="PSI Logo" className="w-8 h-8 object-contain" />
              <span className="font-bold text-gray-800 text-sm">CS Tools</span>
            </div>
          )}
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => setActiveMenu(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-colors ${
                  activeMenu === item.id
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                title={item.label}
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${activeMenu === item.id ? 'text-blue-600' : 'text-gray-400'}`} />
                {sidebarOpen && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {activeMenu === 'cs-report' && renderCSReport()}
          {activeMenu === 'reset-password' && renderResetPassword()}
        </div>
      </div>
    </div>
  );
};

export default CSReportComponent;